import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getPermissions = async (token) => {
  const url = 'admin/permissions';
  const data = await apiGet({ url, token });
  return data;
};

const getPermission = async (token, id) => {
  const url = `admin/permissions/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createPermission = async (token, params) => {
  const url = 'admin/permissions';
  const data = await apiPost({ url, token, objectName: 'permission', params });
  return data;
};

const updatePermission = async (token, id, params) => {
  const url = `admin/permissions/${id}`;
  const data = await apiPatch({ url, token, objectName: 'permission', params });
  return data;
};

const deletePermission = async (token, id) => {
  const url = `admin/permissions/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminPermissions({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getPermissions(token);
  const queryKey = 'admin-permissions';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminPermission({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getPermission(token, id);
  const queryKey = ['admin-permission', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminPermission({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createPermission(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-permissions');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminPermission({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updatePermission(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-permissions');
        queryClient.invalidateQueries(['admin-permission', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminPermission({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deletePermission(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-permissions');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
